import { ref } from 'vue';
import {
  setupFacebookSdk,
  initWhatsAppEmbeddedSignup,
  createMessageHandler,
  isValidBusinessData,
} from 'dashboard/routes/dashboard/settings/inbox/channels/whatsapp/utils';

// Drives Meta's WhatsApp embedded-signup popup (Facebook JS SDK). FB.login()
// resolves an auth `code` while the WABA identifiers (waba_id, phone_number_id)
// arrive separately over a postMessage event — order isn't guaranteed, so we
// hold both and resolve once both are present.
//
// `runEmbeddedSignup` returns the signup credentials; the caller exchanges them
// for an inbox via `inboxes/createWhatsAppEmbeddedSignup` and owns its own UX
// (alerts, navigation, etc). Resolves `null` when the user cancels the popup;
// rejects on SDK load or signup errors. The window listener is scoped to a
// single run, so this is safe to call from anywhere without lifecycle wiring.
//
// WASLO: Split into preloadSdk() + runEmbeddedSignup() for popup safety, exactly
// WASLO: as upstream's own `useFacebookPageConnect` does (see the comment there):
// WASLO: `FB.login` opens a popup and needs the click's transient activation.
// WASLO: Loading the SDK inside the @click handler means the click-time `await`
// WASLO: resolves on the script's `load` task seconds later, by which time Chrome
// WASLO: has expired the activation and silently blocks the popup — no callback,
// WASLO: no error, and the spinner never comes back. Callers warm the SDK on mount
// WASLO: so the click-time `await` resolves within the same task.

// WASLO: A run is bounded in TWO phases, because only the first one is ours.
// WASLO:
// WASLO:   Phase 1 — load the SDK and dispatch FB.login. Nothing in that chain has a
// WASLO:   timeout of its own, and it is where both real failures live: an SDK that
// WASLO:   never becomes ready, and a popup Chrome blocks for lost transient
// WASLO:   activation (no callback, no error). 60 s is already generous for it.
// WASLO:
// WASLO:   Phase 2 — the user is inside Meta's dialog. Meta emits NO progress events:
// WASLO:   every WA_EMBEDDED_SIGNUP message (FINISH, FINISH_ONLY_WABA,
// WASLO:   FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING, CANCEL, error) and the FB.login
// WASLO:   callback are TERMINAL, so nothing can tell us the user is still working.
// WASLO:   Timing this phase out would remove the message listener mid-signup and
// WASLO:   drop the FINISH that follows — the user completes signup and no inbox is
// WASLO:   ever created. Creating a WABA in there legitimately takes many minutes, so
// WASLO:   the bound here is only a last-resort leak stop, not a UX deadline.
const SDK_TIMEOUT_MS = 60000;
const POPUP_TIMEOUT_MS = 900000; // 15 minutes
const TIMEOUT_MESSAGE = 'WhatsApp signup timed out; reload and try again';

export function useWhatsappEmbeddedSignup() {
  const isAuthenticating = ref(false);

  // WASLO: Memoised SDK load, mirroring `useFacebookPageConnect.preloadSdk`.
  // WASLO: Idempotent — call it when the surface that owns the connect button
  // WASLO: mounts. A failed load clears the cache so a later attempt can retry
  // WASLO: instead of being stuck on a rejection.
  let sdkSetupPromise = null;

  const preloadSdk = () => {
    if (!sdkSetupPromise) {
      sdkSetupPromise = setupFacebookSdk(
        window.chatwootConfig?.whatsappAppId,
        window.chatwootConfig?.whatsappApiVersion
      ).catch(error => {
        sdkSetupPromise = null;
        throw error;
      });
    }
    return sdkSetupPromise;
  };

  const runEmbeddedSignup = () => {
    if (isAuthenticating.value) return Promise.resolve(null);
    isAuthenticating.value = true;

    return new Promise((resolve, reject) => {
      let authCode = null;
      let businessData = null;
      let settled = false;
      let messageHandler;

      // WASLO: One timer, re-armed as the run crosses from phase 1 into phase 2 (see
      // WASLO: the constants above). It routes through `settle`, so on expiry the
      // WASLO: button comes back and the caller sees a real error, not a spinner.
      let timeoutId = null;

      const cancelTimeout = () => {
        if (timeoutId) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }
      };

      const settle = (fn, value) => {
        if (settled) return;
        settled = true;
        cancelTimeout(); // WASLO
        window.removeEventListener('message', messageHandler);
        isAuthenticating.value = false;
        fn(value);
      };

      // WASLO
      const armTimeout = ms => {
        cancelTimeout();
        timeoutId = setTimeout(() => {
          settle(reject, new Error(TIMEOUT_MESSAGE));
        }, ms);
      };

      armTimeout(SDK_TIMEOUT_MS); // WASLO: phase 1 starts now

      // Both the auth code and the business data arrive asynchronously and in
      // no fixed order; only resolve once we're holding both.
      const resolveIfReady = () => {
        if (!authCode || !businessData) return;
        settle(resolve, {
          code: authCode,
          business_id: businessData.business_id,
          waba_id: businessData.waba_id,
          phone_number_id: businessData.phone_number_id || '',
        });
      };

      messageHandler = createMessageHandler(data => {
        if (
          data.event === 'FINISH' ||
          data.event === 'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING'
        ) {
          if (!isValidBusinessData(data.data)) {
            settle(reject, new Error('Invalid business data'));
            return;
          }
          businessData = data.data;
          resolveIfReady();
        } else if (data.event === 'CANCEL') {
          settle(resolve, null);
        } else if (data.event === 'error') {
          settle(reject, new Error(data.error_message || 'Signup error'));
        }
      });

      window.addEventListener('message', messageHandler);

      (async () => {
        try {
          // WASLO: `preloadSdk()` instead of `setupFacebookSdk(...)` — when the surface
          // WASLO: warmed the SDK on mount this is an already-resolved promise, so
          // WASLO: FB.login is reached inside the click's activation window.
          await preloadSdk();
          // WASLO: `initWhatsAppEmbeddedSignup` calls FB.login synchronously, so by the
          // WASLO: time it returns the popup has been requested: phase 1 is over and
          // WASLO: the clock now belongs to the user, not to us.
          const login = initWhatsAppEmbeddedSignup(
            window.chatwootConfig?.whatsappConfigurationId
          );
          armTimeout(POPUP_TIMEOUT_MS); // WASLO: phase 2
          authCode = await login;
          cancelTimeout(); // WASLO: FB.login is terminal — nothing left to bound.
          resolveIfReady();
        } catch (error) {
          // FB.login() rejects with 'Login cancelled' when the user dismisses
          // the popup — treat it as a cancel rather than an error.
          if (error.message === 'Login cancelled') {
            settle(resolve, null);
          } else {
            settle(reject, error);
          }
        }
      })();
    });
  };

  return { isAuthenticating, preloadSdk, runEmbeddedSignup }; // WASLO: + preloadSdk
}
